
import React, { useEffect, useState, useRef } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { InvoiceType } from '../../../../types/types';
import Error from '../../../../components/ui/Error/Error';
import Button from '../../../../components/ui/Button/Button';
import { notify } from '../../../../components/ui/Toast/Toast';
import TextInput from '../../../../components/ui/Inputs/TextInput';
import TextAreaInput from '../../../../components/ui/Inputs/TextAreaInput';
import DateInput from '../../../../components/ui/Inputs/DateInput/DateInput';
import ContainerCard from '../../../../components/ui/ContainerCard/ContainerCard';
import SelectInput from '../../../../components/ui/Inputs/SelectInput/SelectInput';
import { getStatusFromId, getStatusId, getTypeFromId, getTypeId } from '../../functions';
import { fetchSelectData, frenchFormat, getErrors, standardFormat } from '../../../../utils';
import { BankActions, DealActions, InvoiceActions, CustomerActions } from '../../../../actions';

function CreateNewInvoice({userData, setIsInvoicePopupOpen, invoiceToBeUpdated, setView, refreshData}) {
    // -- states --
    const [disabled, setDisabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState(null);
    const [deals, setDeals] = useState(null);
    const [banks, setBanks] = useState(null);
    const [dealRecipients, setDealRecipients] = useState({ options: [], hasNextPage: false });
    const [recipientsLoading, setRecipientsLoading] = useState(false);
    // const [services, setServices] = useState([]);
    // const [types, setTypes] = useState([]);

    const vat = useSelector((state) => state.GlobalsReducer.vat);

    // -- react hook form --
    const defaultValues = {
        status: {name :"Prévisionnelle"},
        deal_id:null ,
        billing_date: dayjs(),
        billable: null,
        expenses:null ,
        bank_name:null,
        type:null ,
        billing_adress:null ,
        due_date: dayjs().add(1, 'month'),
        billable_vat_rate:vat ,
        expenses_vat_rate:vat ,
        notes: '' ,
        customer_id: null,
        billed_entity: null
    };

    const {handleSubmit, control, reset, setValue, trigger, formState: {isValid}} = useForm({defaultValues: defaultValues});

    // Watch the deal field to enable/disable and load recipients
    const selectedDeal = useWatch({ control, name: 'deal' });
    // Surveiller le type de facture pour adapter les validations
    const invoiceType = useWatch({ control, name: 'type' });
    const billingDate = useWatch({ control, name: 'billing_date' });
    const previousTypeRef = useRef(null);
    const isBeforeBillingDate = (date) => billingDate && date ? dayjs(date).isBefore(dayjs(billingDate), 'day') : false;

    const handleGetRecipients = async (params) => {
        const result = await CustomerActions.getAllMergedCustomersContractors(userData, `id=${params.id}&type=${params.type}`, 1);

        if (result.success) {
            return result.data.results[0];
        } else {
            notify('Erreur lors de la récupération du destinataire');
            return null;
        }
    }

    const handleGetDealRecipients = async (value) => {
        if (!selectedDeal?.id) {
            setDealRecipients({ options: [], hasNextPage: false });
            return;
        }

        setRecipientsLoading(true);
        const result = await CustomerActions.getDealRecipients(userData, selectedDeal.id);

        if (result.success) {
            // Filtrer les résultats par la recherche si nécessaire
            let filteredData = result.data;

            if (value && value.trim() !== '') {
                const searchLower = value.toLowerCase();
                filteredData = result.data.filter(item => 
                    item.name.toLowerCase().includes(searchLower)
                );
            }
            
            // Formater les données pour correspondre au format attendu par SelectInput
            setDealRecipients({
                options: filteredData,
                hasNextPage: false
            });
        } else {
            notify('Erreur lors de la récupération des destinataires');
            setDealRecipients({ options: [], hasNextPage: false });
        }
        setRecipientsLoading(false);
    };

    // -- fill invoice values in form for update --
    useEffect(() => {
        if (invoiceToBeUpdated && invoiceToBeUpdated.id) {
            // Fonction async pour charger le destinataire
            const loadRecipientAndReset = async () => {
                let selectedBilledEntity = null;

                // Charger le destinataire depuis l'API avec les filtres id et type
                if (invoiceToBeUpdated.billed_entity_id && invoiceToBeUpdated.billed_entity_type) {
                    selectedBilledEntity = await handleGetRecipients({
                        id: invoiceToBeUpdated.billed_entity_id,
                        type: invoiceToBeUpdated.billed_entity_type
                    });
                }

                // Remplir le formulaire
                reset({
                    status: {name: getStatusFromId(invoiceToBeUpdated?.status)},
                    deal: invoiceToBeUpdated?.deal,
                    billing_date: invoiceToBeUpdated?.billing_date ,
                    billable: (+invoiceToBeUpdated?.billable).toString(),
                    expenses: (+invoiceToBeUpdated?.expenses).toString(),
                    bank: {name: invoiceToBeUpdated?.bank?.name},
                    type: {name: getTypeFromId(invoiceToBeUpdated?.type)},
                    billing_address: invoiceToBeUpdated?.billing_address,
                    due_date: invoiceToBeUpdated?.due_date,
                    billable_vat_rate: (+invoiceToBeUpdated?.billable_vat_rate * 100),
                    expenses_vat_rate: (+invoiceToBeUpdated?.expenses_vat_rate * 100),
                    customer_id: invoiceToBeUpdated?.deal?.customer?.id,
                    notes: invoiceToBeUpdated?.notes,
                    billed_entity: selectedBilledEntity || null,
                });
            };

            loadRecipientAndReset();
        }
    }, [invoiceToBeUpdated]);

    // -- dropdowns values --
    // const typesDropdownValues = [
    //     {name: 'Honoraires'},
    //     {name: 'Mise à disposition de personnel'},
    //     {name: 'Frais'}
    // ];

    // ↓ status dropdown values
    const statusDropdownValues = [
        {name: 'Prévisionnelle'},
        {name: 'À éditer'},
        {name: 'À valider'},
        {name: 'À émettre'},
        {name: 'Émise'},
        {name: 'Partiellement recouvrée'},
        {name: 'Recouvrée'},
        {name: 'Annulée'}
    ];

    // -- functions that get data for Select Inputs --
    const handleGetBanks = async (search, page = 1) => {
        await fetchSelectData({action: BankActions.getAllBanks, search:`&search=${search}&active=true`, page: page, setter: setBanks, userData, resultPath: 'data'});
    };

    const handleGetDeals = async (search, page = 1) => {

        await fetchSelectData({action: DealActions.getAllDeals, search:`&type=business&display=${userData.userProfileName}&search=${search}`, page: page, setter: setDeals, userData, resultPath: 'data'});
    };

    const typesDropdownValues = Object.values(InvoiceType).map((type) => ({name: type}));

    // const handleGetServices = async (search, page = 1) => {

    //     await fetchSelectData({action: ServicesActions.getAllServices, search:`&search=${search}`, page: page, setter: setServices, userData: userData.userToken, resultPath: 'data'});
    // };

    useEffect(() => {
        if (!invoiceToBeUpdated && billingDate) {
            setValue('due_date', dayjs(billingDate).add(1, 'month').format('YYYY-MM-DD'), { shouldValidate: true });
            trigger('due_date');
        }
    }, [billingDate, invoiceToBeUpdated, setValue, trigger]);

    useEffect(() => {
        if (!userData) {
            return;
        }
        // ↓ deals dropdown values
        (async () => {
            await handleGetDeals('', 1);
        })();
        // ↓ banks dropdown values
        (async () => {
            await handleGetBanks('', 1);
        })();
        // ↓ services dropdown values
        // (async () => {
        //     await handleGetServices('', 1);
        // })();
    }, []);

    // Load recipients when a deal is selected
    useEffect(() => {
        // Reset billed_entity whenever the deal changes (including when cleared)
        setValue('billed_entity', null);
        
        if (selectedDeal?.id) {
            handleGetDealRecipients('');
        }
        else {
            // Reset recipients when no deal is selected
            setDealRecipients({ options: [], hasNextPage: false });
        }
    }, [selectedDeal?.id]);

    // Réinitialiser les valeurs selon le type de facture
    useEffect(() => {
        const currentType = invoiceType?.name;
        
        // Éviter de réexécuter si le type n'a pas changé
        if (!currentType || currentType === previousTypeRef.current) {
            return;
        }

        previousTypeRef.current = currentType;

        if (currentType === InvoiceType.HONORAIRES) {
            // Type "Honoraires" : mettre expenses à 0
            setValue('expenses', 0, { shouldValidate: false });
            trigger('expenses');
            trigger('billable');
        } else if (currentType === InvoiceType.FRAIS) {
            // Type "Frais" : mettre billable à 0
            setValue('billable', 0, { shouldValidate: false });
            trigger('billable');
            trigger('expenses');
        } else if (currentType === InvoiceType.HONORAIRES_ET_FRAIS) {
            // Type "Honoraires et frais" : réinitialiser si nécessaire
            trigger('billable');
            trigger('expenses');
        }
    }, [invoiceType?.name]);

    //TODO: How to get Honoraire type ID ?
    // useEffect(() => {
    //     if (!services.options) return;

    //     setTypes([
    //         {name: 'Honoraires'},
    //         ...services.options.map((service) => ({name: `OD: ${service.short_name}`}))
    //     ])
    // }, [services]);

    const handleSendForValidation = async () => {

        const result = await InvoiceActions.updateInvoiceById(userData, invoiceToBeUpdated.id, {status: 100});

        if (result.success) {
            await refreshData();
            notify('Votre facture a été envoyée pour validation');
        }
        else {
            notify('Votre facture n\'a pas pu être envoyée pour validation');
        }

        setIsInvoicePopupOpen(false);
    };

    const handleSendForIssue = async () => {

        const result = await InvoiceActions.updateInvoiceById(userData, invoiceToBeUpdated.id, {status: 200});

        if (result.success) {
            await refreshData();
            notify('Votre facture a été envoyée à émettre');
        }
        else {
            notify('Votre facture n\'a pas pu être envoyée à émettre');
        }

        setIsInvoicePopupOpen(false);
    };

    const onButtonClick = () => {
        if (invoiceToBeUpdated?.status === 50) {
            handleSendForValidation();
        }
        else if (invoiceToBeUpdated?.status === 100) {
            handleSendForIssue();
        }
        else return null;
    };

    const getTitle = () => {
        if (invoiceToBeUpdated && invoiceToBeUpdated?.id) {
            switch (invoiceToBeUpdated?.status) {
            case 0:
                return 'Éditer la facture';
            case 50:
                return 'Éditer la facture';
            case 100:
                return 'Éditer la facture';
            case 200:
                return 'Émettre la facture';
            default:
                return 'Éditer la facture';
            }
        }
        else {
            return 'Nouvelle Facture';
        }
    }

    const sendButtonDisplay = invoiceToBeUpdated?.status === 50 || invoiceToBeUpdated?.status === 100;

    // -- handler functions --
    const onSubmit = async (data) => {
        setDisabled(true);
        setLoading(true);
        setErrors(null);

        // Normaliser les valeurs selon le type de facture
        const invoiceTypeName = data.type?.name;
        let billableValue = data.billable !== null && data.billable !== '' ? Number(data.billable) : null;
        let expensesValue = data.expenses !== null && data.expenses !== '' ? Number(data.expenses) : null;

        if (invoiceTypeName === InvoiceType.HONORAIRES) {
            expensesValue = 0;
        } else if (invoiceTypeName === InvoiceType.FRAIS) {
            billableValue = 0;
        }

        // ↓ body to send to the post/patch request (see API documentation)
        const invoice = {
            status: getStatusId(data.status.name),
            deal_id: invoiceToBeUpdated?.deal_id ? data.deal_id : data.deal.id,
            billing_date: standardFormat(data.billing_date),
            billable: billableValue,
            expenses: expensesValue,
            bank_name: data.bank.name,
            type: invoiceToBeUpdated?.type ? getTypeId(data.type.name) : getTypeId(data.type.name),
            billing_address: data.billing_address,
            due_date: standardFormat(data.due_date),
            billable_vat_rate: (data.billable_vat_rate / 100).toString() ,
            expenses_vat_rate: (data.expenses_vat_rate / 100).toString(),
            customer_id: invoiceToBeUpdated?.customer_id ? data.customer_id : data?.deal?.customer?.id,
            notes: data.notes ?? '',
            subsidiary: data.deal.subsidiary?.name ?? '',
            billed_entity_type: data.billed_entity?.type,
            billed_entity_id: data.billed_entity?.id,
        };

        // ↓ post - new invoice
        if (!invoiceToBeUpdated) {

            const result = await InvoiceActions.createInvoice(userData, invoice);

            if (!result.success) {
                setErrors(getErrors(result));
            }
            else {
                await refreshData();
                notify('Votre facture à bien été créée');

                setView(false);
                reset();
            }

            setLoading(false);
            setDisabled(false);
        }
        // ↓ patch - update invoice
        else {

            const result = await InvoiceActions.updateInvoiceById(userData, invoiceToBeUpdated.id, invoice);

            if (!result.success) {
                setErrors(getErrors(result));
            }
            else {
                notify('Votre facture à bien été modifiée');
                await refreshData();

                setView(false);
                reset();
            }

            setLoading(false);
            setDisabled(false);
        }
    };

    return (
        <ContainerCard
            title={getTitle()}
            buttonContent={invoiceToBeUpdated?.status === 50 ? 'Envoyer à valider' : "Envoyer à émettre"}
            onButtonClick={onButtonClick}
            coloredButtonDisabled={(!deals || !banks) ? true : !isValid}
            coloredButtonDisplayed={sendButtonDisplay}
            overflow='visible'
        >
            <div>
                {
                    ((invoiceToBeUpdated && invoiceToBeUpdated.status < 300) || !invoiceToBeUpdated) ?
                        (<div className="flex flex-row justify-between py-5 pr-4 pl-4">
                            <div>
                                <div className="flex mb-2">
                                    <Controller
                                        name="status"
                                        control={control}
                                        rules={{required: {
                                            value: true,
                                            message: 'Veuillez sélectionner un statut.'
                                        }}}
                                        render={({
                                            field: {onChange, value, ref},
                                            fieldState: {error}
                                        }) => (
                                            <SelectInput
                                                label='Statut'
                                                placeholder='Statut'
                                                inputRef={ref}
                                                value={value}
                                                error={error}
                                                options={statusDropdownValues}
                                                onChange={onChange}
                                                loading={!statusDropdownValues}
                                                isSearchable={true}
                                                fetchFunction={handleGetBanks}
                                                labelKeys={['name']}
                                                required={true}
                                                disabled={true}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="flex mb-2">
                                    <Controller
                                        name="deal"
                                        control={control}
                                        rules={{required: {
                                            value: true,
                                            message: 'Veuillez sélectionner une affaire.'
                                        }}}
                                        render={({
                                            field: {onChange, value, ref},
                                            fieldState: {error}
                                        }) => (
                                            <SelectInput
                                                label='Affaire'
                                                placeholder='Affaire'
                                                inputRef={ref}
                                                value={value}
                                                error={error}
                                                options={deals?.options || []}
                                                hasNextPage={deals?.hasNextPage || false}
                                                fetchFunction={handleGetDeals}
                                                loading={!deals}
                                                onChange={onChange}
                                                labelKeys={['short_name']}
                                                isSearchable={true}
                                                required={true}
                                                disabled={invoiceToBeUpdated?.status === 0 ? false : invoiceToBeUpdated?.status === 200 && !disabled ? true  : disabled}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="flex mb-2">
                                    <Controller
                                        name="billing_date"
                                        control={control}
                                        rules={{required: {
                                            value: true,
                                            message: 'Veuillez sélectionner une date.'
                                        }}}
                                        render={({
                                            field: {onChange, value},
                                            fieldState: {error}
                                        }) => (
                                            <DateInput
                                                label={'Date de facturation'}
                                                onChange={onChange}
                                                value={frenchFormat(value)}
                                                error={error}
                                                required={true}
                                                disabled={invoiceToBeUpdated?.status === 0 ? false : invoiceToBeUpdated?.status === 200 && !disabled ? true  : disabled}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="flex mb-2">
                                    <Controller
                                        name="billable"
                                        control={control}
                                        rules={{
                                            validate: (value) => {
                                                const type = invoiceType?.name;
                                                if (!type) return true;
                                                
                                                if (type === InvoiceType.FRAIS) {
                                                    // Pour "Frais", billable doit être 0
                                                    return (value === 0 || value === null || value === '0' || value === '') || '';
                                                }
                                                
                                                if (type === InvoiceType.HONORAIRES || type === InvoiceType.HONORAIRES_ET_FRAIS) {
                                                    // Pour "Honoraires" ou "Honoraires et frais", billable doit être > 0
                                                    const numValue = value === '' || value === null ? null : Number(value);
                                                    return (numValue !== null && numValue > 0) || '';
                                                }
                                                
                                                return true;
                                            }
                                        }}
                                        render={({
                                            field: {onChange, value},
                                            fieldState: {error}
                                        }) => (
                                            <TextInput
                                                label={'Honoraires Montant HT [€]'}
                                                onChange={(e) => {
                                                    const numValue = e === '' || e === null ? null : Number(e);
                                                    onChange(numValue);
                                                }}
                                                value={value?.toString() ?? ''}
                                                error={error}
                                                required={invoiceType?.name !== InvoiceType.FRAIS}
                                                disabled={invoiceToBeUpdated?.status === 0 ? false : invoiceToBeUpdated?.status === 200 && !disabled ? true : disabled || invoiceType?.name === InvoiceType.FRAIS}
                                                placeholder={invoiceType?.name === InvoiceType.FRAIS ? "0 (non applicable)" : "Honoraires Montant HT"}
                                                type="number"
                                            />
                                        )}
                                    />
                                </div>
                                <div className="flex mb-2">
                                    <Controller
                                        name="expenses"
                                        control={control}
                                        rules={{
                                            validate: (value) => {
                                                const type = invoiceType?.name;
                                                if (!type) return true;
                                                
                                                if (type === InvoiceType.HONORAIRES) {
                                                    // Pour "Honoraires", expenses doit être 0
                                                    return (value === 0 || value === null || value === '0' || value === '') || '';
                                                }
                                                
                                                if (type === InvoiceType.FRAIS || type === InvoiceType.HONORAIRES_ET_FRAIS) {
                                                    // Pour "Frais" ou "Honoraires et frais", expenses doit être > 0
                                                    const numValue = value === '' || value === null ? null : Number(value);
                                                    return (numValue !== null && numValue > 0) || '';
                                                }
                                                
                                                return true;
                                            }
                                        }}
                                        render={({
                                            field: {onChange, value},
                                            fieldState: {error}
                                        }) => (
                                            <TextInput
                                                label={'Frais Montant HT [€]'}
                                                onChange={(e) => {
                                                    const numValue = e === '' || e === null ? null : Number(e);
                                                    onChange(numValue);
                                                }}
                                                value={value?.toString() ?? ''}
                                                error={error}
                                                required={invoiceType?.name !== InvoiceType.HONORAIRES}
                                                disabled={invoiceToBeUpdated?.status === 0 ? false : invoiceToBeUpdated?.status === 200 && !disabled ? true : disabled || invoiceType?.name === InvoiceType.HONORAIRES}
                                                placeholder={invoiceType?.name === InvoiceType.HONORAIRES ? "0 (non applicable)" : "Frais Montant HT"}
                                                type="number"
                                            />
                                        )}
                                    />
                                </div>
                                <div className="flex mb-2">
                                    <Controller
                                        name="bank"
                                        control={control}
                                        rules={{required: {
                                            value: true,
                                            message: 'Veuillez sélectionner une banque.'
                                        }}}
                                        render={({
                                            field: {onChange, value, ref},
                                            fieldState: {error}
                                        }) => (
                                            <SelectInput
                                                label='Compte bancaire'
                                                placeholder='Compte bancaire'
                                                inputRef={ref}
                                                value={value}
                                                error={error}
                                                options={banks?.options || []}
                                                hasNextPage={banks?.hasNextPage || false}
                                                loading={!banks}
                                                onChange={onChange}
                                                isSearchable={true}
                                                fetchFunction={handleGetBanks}
                                                labelKeys={['name']}
                                                required={true}
                                                disabled={invoiceToBeUpdated?.status === 0 ? false : invoiceToBeUpdated?.status === 200 && !disabled ? true  : disabled}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col w-full lg:w-1/2">
                                <div className="flex mb-2">
                                    <Controller
                                        name="type"
                                        control={control}
                                        rules={{required: {
                                            value: true,
                                            message: 'Veuillez sélectionner un type de facture.'
                                        }}}
                                        render={({
                                            field: {onChange, value, ref},
                                            fieldState: {error}
                                        }) => (
                                            <SelectInput
                                                label='Type'
                                                placeholder='Type'
                                                inputRef={ref}
                                                value={value}
                                                error={error}
                                                options={typesDropdownValues}
                                                loading={!typesDropdownValues}
                                                onChange={onChange}
                                                isSearchable={true}
                                                labelKeys={['name']}
                                                required={true}
                                                disabled={invoiceToBeUpdated?.status === 0 ? false : invoiceToBeUpdated?.status === 200 && !disabled ? true  : disabled}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="flex flex-col items-start mb-2">
                                    <Controller
                                        name="billed_entity"
                                        control={control}
                                        rules={{required: {
                                            value: true,
                                            message: 'Veuillez sélectionner un destinataire.'
                                        }}}
                                        render={({
                                            field: {onChange, value, ref},
                                            fieldState: {error}
                                        }) => (
                                            <>
                                                <SelectInput
                                                    label='Destinataire'
                                                    placeholder={selectedDeal ? 'Destinataire' : 'Veuillez d\'abord sélectionner une affaire'}
                                                    inputRef={ref}
                                                    value={value}
                                                    error={error}
                                                    hasNextPage={dealRecipients?.hasNextPage}
                                                    options={dealRecipients?.options || []}
                                                    loading={recipientsLoading}
                                                    fetchFunction={handleGetDealRecipients}
                                                    isSearchable={true}
                                                    disabled={!selectedDeal || disabled}
                                                    onChange={onChange}
                                                    labelKeys={['name']}
                                                    required={true}
                                                />
                                                {value && !value.is_synced && (
                                                    <div className="flex items-start mt-1 text-xs text-orange-500">
                                                        <span className="mr-1">⚠️</span>
                                                        <span>
                                                            Cette destinataire n&apos;est pas synchronisée avec PennyLane.
                                                            La facture ne pourra être émise qu&apos;après synchronisation.
                                                        </span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    />
                                </div>
                                <div className="flex mb-2">
                                    <Controller
                                        name="due_date"
                                        control={control}
                                        rules={{
                                            required: {
                                                value: true,
                                                message: 'Veuillez sélectionner une date.'
                                            },
                                            validate: (value) => !isBeforeBillingDate(value) || "La date d'échéance ne peut pas être avant la date de facturation."
                                        }}
                                        render={({
                                            field: {onChange, value},
                                            fieldState: {error}
                                        }) => (
                                            <DateInput
                                                label={"Date d'écheance"}
                                                onChange={onChange}
                                                value={frenchFormat(value)}
                                                error={error}
                                                required={true}
                                                disabled={invoiceToBeUpdated?.status === 0 ? false : invoiceToBeUpdated?.status === 200 && !disabled ? true  : disabled}
                                                disabledDate={isBeforeBillingDate}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="flex mb-2">
                                    <Controller
                                        name="billable_vat_rate"
                                        control={control}
                                        rules={{
                                            required: {
                                                value: true,
                                                message: 'Veuillez saisir la tva d\'honoraires'
                                            }
                                        }}
                                        render={({
                                            field: {onChange, value},
                                            fieldState: {error}
                                        }) => (
                                            <TextInput
                                                label={'Honoraires TVA [%]'}
                                                onChange={onChange}
                                                value={value}
                                                error={error}
                                                required={true}
                                                disabled={invoiceToBeUpdated?.status === 0 ? false : invoiceToBeUpdated?.status === 200 && !disabled ? true  : disabled}
                                                placeholder="Honoraires TVA"
                                                type="number"
                                            />
                                        )}
                                    />
                                </div>
                                <div className="flex mb-2">
                                    <Controller
                                        name="expenses_vat_rate"
                                        control={control}
                                        rules={{required: {
                                            value: true,
                                            message: 'Veuillez saisir la tva des frais'
                                        }}}
                                        render={({
                                            field: {onChange, value},
                                            fieldState: {error}
                                        }) => (
                                            <TextInput
                                                label={'Frais TVA [%]'}
                                                onChange={onChange}
                                                value={value}
                                                error={error}
                                                required={true}
                                                disabled={invoiceToBeUpdated?.status === 0 ? false : invoiceToBeUpdated?.status === 200 && !disabled ? true  : disabled}
                                                placeholder="Frais TVA"
                                                type="number"
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        </div>)
                        : null
                }
                <div className="px-5">
                    <div className={(invoiceToBeUpdated?.status < 300 || !invoiceToBeUpdated) ? 'py-4 border-t border-borderGrey' : 'py-4'}>
                        <Controller
                            name="notes"
                            control={control}
                            render={({
                                field: {onChange, value}
                            }) => (
                                <>
                                    <div className="font-normal text-sm text-[#646464] mb-2">Veuillez ajouter une note</div>
                                    <TextAreaInput
                                        placeholder={'Tapez votre texte'}
                                        maxLength={100}
                                        onChange={onChange}
                                        value={value}
                                        disabled={disabled}
                                    />
                                </>
                            )}
                        />
                    </div>
                </div>
            </div>
            <div className="flex flex-col justify-center mt-6">
                <div className="flex justify-center">
                    <Button
                        onClick={() => setView(false)}
                        type={'secondary'}
                        content={'Annuler'}
                        disabled={disabled}
                    />
                    {
                        (!invoiceToBeUpdated || invoiceToBeUpdated.date && !invoiceToBeUpdated.id) ?
                            <Button
                                type={'primary'}
                                content={'Ajouter la facture'}
                                onClick={handleSubmit(onSubmit)}
                                loading={loading}
                                disabled={!isValid}
                            /> : null
                    }
                    {
                        (invoiceToBeUpdated && invoiceToBeUpdated.id) ?
                            <Button
                                type={'primary'}
                                content={"Enregistrer"}
                                onClick={handleSubmit(onSubmit)}
                                loading={loading}
                                disabled={(!deals || !banks) ? true : !isValid}
                            /> : null
                    }
                </div>
                <Error errors={errors}/>
            </div>
        </ContainerCard>
    );
}

export default CreateNewInvoice;
